import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Stack, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatRelative } from "date-fns";
import { capitalize } from "lodash";
import { useRef } from "react";
import ReactPlayer from "react-player";
import { useEventListener } from "usehooks-ts";

import { File } from "@/components/graphql/schema.graphql";
import { IconButton, Typography } from "@/components/ui-v2";
import { CheckerboardBackground } from "@/components/ui-v2/Backgrounds/Checkerboard";
import { getObjectMetadata, getObjectType } from "@/utils/libraryObjectUtils";
import { getSrcSet, normalizeThumbnails } from "@/utils/thumbnails";

import { DriveDetailedFileWithWorkspaceFragment } from "../drive/queries.graphql";
import { ModelView } from "../mesh/ModelView";
import { AudioPreview } from "./AudioPreview";
import { classes } from "./constants";
import { GalleryImagePreview } from "./GalleryImagePreview";
import { GalleryObject } from "./types";

export const GALLERY_WINDOW_Z_INDEX = 1500;

interface GalleryWindowProps {
  object: GalleryObject;
  onClose: () => void;
  onArrowKeyPress?: (e: KeyboardEvent) => void;
  listenToKeyPress?: boolean;
}

const CloseButtonWrapper = styled("div")({
  position: "absolute",
  top: "-1px",
  right: "-1px",
  zIndex: 41,
});

export const GalleryWindow = (props: GalleryWindowProps) => {
  const theme = useTheme();
  const rootRef = useRef<HTMLElement>(null);

  const { object, onClose, onArrowKeyPress, listenToKeyPress = false } = props;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!listenToKeyPress) {
      return;
    }

    switch (e.key) {
      case "Escape":
      case " ":
        e.preventDefault();
        onClose();
        break;
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
        // We don't want to allow multiselection in the background when user can't view what he's selecting
        if (!e.shiftKey && onArrowKeyPress) {
          onArrowKeyPress(e);
        }
        break;
      default:
        break;
    }
  };

  useEventListener("keydown", handleKeyDown, rootRef);

  const handleLoad = () => {
    rootRef.current?.focus();
  };

  if (!object) {
    return null;
  }

  const objectType = getObjectType(object);
  const { icon } = getObjectMetadata(objectType);

  let mainContent: JSX.Element | null = null;

  if (object.__typename === "File" && objectType === "video" && object.url) {
    mainContent = (
      <ReactPlayer
        url={object.url}
        controls
        playing
        width="100%"
        height="100%"
        muted
      />
    );
  } else if (
    object.__typename === "File" &&
    objectType === "mesh" &&
    object.url
  ) {
    mainContent = <ModelView url={object.url} />;
  } else if (
    object.__typename === "File" &&
    objectType === "audio" &&
    object.url
  ) {
    mainContent = (
      <AudioPreview url={object.url} peaks={object.metadata?.peaks} />
    );
  } else {
    let imageUrl: string | null = null;
    let imageSrcSet: string = "";
    let fileMetadata: File["metadata"] | undefined;
    let attachmentUrls: File["attachmentUrls"] | undefined;
    let sourceUrl: string | undefined;
    if (object.__typename === "File") {
      imageUrl =
        objectType === "image" ? object.url : object.previewUrl ?? null;
      if (imageUrl) {
        imageSrcSet = getSrcSet({
          url: imageUrl,
          metadata: object.metadata
            ? { width: object.metadata.width ?? undefined }
            : undefined,
          thumbnails: normalizeThumbnails(object.thumbnails) ?? undefined,
        });
      }
      // Sometimes, we won't have the full file info (in the Drive notably). We must manipulate the type here when that happens.
      const inferenceParameters = (
        object as unknown as Pick<
          Partial<DriveDetailedFileWithWorkspaceFragment>,
          "inferenceParameters"
        >
      ).inferenceParameters;
      const guidanceFile = inferenceParameters?.files?.[0];
      sourceUrl = guidanceFile?.transformedUrl || guidanceFile?.url;

      fileMetadata = object.metadata ?? undefined;
      attachmentUrls = object.attachmentUrls ?? undefined;
    } else if (object.__typename === "Asset") {
      imageUrl = object.previewUrl ?? null;
    }
    if (imageUrl) {
      mainContent = (
        <GalleryImagePreview
          imageUrl={imageUrl}
          imageSrcSet={imageSrcSet}
          sourceUrl={sourceUrl}
          fileMetadata={fileMetadata}
          attachmentUrls={attachmentUrls}
          onLoad={handleLoad}
        />
      );
    } else {
      mainContent = (
        <CheckerboardBackground
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              maxWidth: "75%",
              gap: 10,
            }}
          >
            <Box sx={{ "& svg": { fontSize: "3em" } }}>{icon}</Box>
            <Stack>
              <Typography variant="title1">{object.name}</Typography>
              <Typography variant="large">
                Created by {object.createdByPrincipal?.displayName || "Unknown"}
              </Typography>
              <Typography
                variant="large"
                fontWeight="light"
                color="secondary.200"
              >
                {capitalize(formatRelative(object.createdAt, new Date()))}
              </Typography>
            </Stack>
          </Stack>
        </CheckerboardBackground>
      );
    }
  }

  return (
    <Box
      tabIndex={-1}
      ref={rootRef}
      sx={{
        position: "fixed",
        left: theme.spacing(4),
        top: theme.spacing(14),
        zIndex: GALLERY_WINDOW_Z_INDEX,
        width: "calc(100vw - 272px)",
        height: "calc(100vh - 128px)",
        border: "1px solid",
        borderColor: "secondary.6",
        borderRadius: "4px",
        backgroundColor: "secondary.8",
        boxShadow: "0px 7px 32px 0px rgba(0, 0, 0, 0.35)",
        outline: "none",
        animation: "modal 150ms ease-in-out forwards",
      }}
      className={classes.root}
    >
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        <CloseButtonWrapper>
          <IconButton
            size="large"
            aria-label="Close Preview"
            onClick={onClose}
            sx={{
              borderRadius: theme.spacing(0, 1),
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </CloseButtonWrapper>
        {mainContent}
      </Box>
    </Box>
  );
};
